import { friendly } from './copy';
// What the screens say, worked out from the sim (2026-10-25, ui branch). Pure, no DOM, so tests/ui.test.ts can check
// the words and numbers against real bot matches: team names, the army cards, what each class beats, the phase line
// with the hard-end warning, and the end card's stats.
import { BASE, BOSS, CLASS, ClassId, FACTION, FACTIONS, FactionId, HERO, HEROES, HERO_RULES, HeroId, heroStats } from '../../sim/defs';
import type { Sim } from '../../sim/sim';

/** Team colors, locked in STYLE.md (red, blue, bronze-gold, teal; never green). Same order as main.ts TEAM_COLORS. */
export const TEAM = ['#d8433a', '#3a72d8', '#d6a631', '#2fa89a'];
export const TEAM_NAME = ['Red', 'Blue', 'Gold', 'Teal'];
export const CLASS_LABEL: Record<ClassId, string> = {
  footman: 'Footman', line: 'Line', spear: 'Spear', ranged: 'Ranged', cavalry: 'Cavalry', siege: 'Siege', support: 'Support', elite: 'Elite', hero: 'Hero', ram: 'Ram',
};
/** What a class is for, from the counter table (defs.ts classBonus, ROSTER.md section 2). */
export const CLASS_ROLE: Record<ClassId, string> = {
  footman: 'Your first soldiers',
  line: 'Beats Spear, shrugs off arrows',
  spear: 'Beats Cavalry',
  ranged: 'Beats Spear and Support',
  cavalry: 'Beats Ranged, Siege and Support',
  support: 'Heals and hurries nearby allies',
  siege: 'Hits groups from far away',
  elite: 'Your best soldier',
  hero: 'Leads your army',
  ram: 'Breaks the wall you attack',
};

export const mmss = (s: number) => { const t = Math.max(0, Math.floor(s)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };

/** A player's name to the viewer: "You" for your seat, else the team color. */
export const who = (p: number, me: number) => (p === me ? 'You' : TEAM_NAME[p]);

export type ArmyCard = { id: FactionId; name: string; trait: string; detail: string; sprite: number };
/** The eight armies as the picker shows them: name, the mechanic's name, and what it does in one line. `sprite` is the
 *  army's portrait in assets/ui/armies.webp (FACTIONS order, cut from concept/r16's footman sheets). */
export const ARMY_CARDS: ArmyCard[] = FACTIONS.map((id, sprite) => {
  const m = FACTION[id].mechanic, k = m.indexOf(':');
  const d = m.slice(k + 1).trim();
  return { id, name: FACTION[id].name, trait: m.slice(0, k).trim(), detail: friendly(d[0].toUpperCase() + d.slice(1)), sprite };
});

export type GiantView = { up: boolean; hp: number; next: number };
/** The center's giant as the HUD shows it: standing or not, its health as a share, when the next wave wakes. */
export function giantView(sim: Sim): GiantView {
  const g = sim.boss;
  return { up: g >= 0, hp: g >= 0 ? sim.hp[g] / sim.maxHp[g] : 0, next: sim.nextBossAt() };
}

/** The line under the clock. Bases are safe, then open; a minute before the hard end it warns (`warn`), then it says
 *  the end has come (`final`). Between, the center's giant (2026-10-26): while it stands the bar is its health, else
 *  the line counts down to the next wave. */
export function phaseLine(time: number, giant?: GiantView): { text: string; tone: 'calm' | 'warn' | 'final'; left: number } {
  // `left`: the share of this phase still to run (the clock plaque's bar), 0 once the final minutes come
  const open = Math.max(0, (BASE.decayFrom - time) / (BASE.decayFrom - BASE.safeUntil));
  if (time < BASE.safeUntil) return { text: `Safe ${mmss(BASE.safeUntil - time)}`, tone: 'calm', left: (BASE.safeUntil - time) / BASE.safeUntil };
  if (giant && time < BASE.decayFrom - 60) {
    if (giant.up) return { text: 'Giant awake', tone: 'calm', left: giant.hp };
    if (giant.next - time <= BOSS.every) return { text: `Giant ${mmss(giant.next - time)}`, tone: 'calm', left: (giant.next - time) / BOSS.every };
  }
  if (time < BASE.decayFrom - 60) return { text: `Crumble ${mmss(BASE.decayFrom)}`, tone: 'calm', left: open };
  if (time < BASE.decayFrom) return { text: `Crumble in ${mmss(BASE.decayFrom - time)}`, tone: 'warn', left: open };
  return { text: 'Bases crumble', tone: 'final', left: 0 };
}

export type SkillView = { state: 'locked' | 'down' | 'stunned' | 'cooldown' | 'short' | 'ready'; sweep: number; secs: number; mana: number; level: number };
/** What a hero skill button shows, from the sim alone (so a paused clock freezes it and tests can drive it). `sweep` is
 *  the share of the cooldown still to run (the dark wedge over the icon). `ready` is exactly when Sim.canCast agrees. */
export function skillView(sim: Sim, p: number, k: 0 | 1): SkillView | null {
  const h = sim.players[p]?.hero;
  if (!h) return null;
  const sk = HERO[h.id].skills[k], wait = h.ready[k] - sim.time;
  const sweep = wait > 0 ? Math.min(1, wait / sk.cd) : 0, secs = Math.max(0, Math.ceil(wait));
  const base = { sweep, secs, mana: sk.mana, level: sk.level };
  if (h.level < sk.level) return { ...base, state: 'locked', sweep: 0, secs: 0 };
  if (h.i < 0) return { ...base, state: 'down' };
  if (wait > 0) return { ...base, state: 'cooldown' };
  if (sim.stunT[h.i] > sim.time) return { ...base, state: 'stunned' };
  return { ...base, state: h.mana < sk.mana ? 'short' : 'ready' };
}

export type HeroView = { alive: boolean; level: number; hp: number; mana: number; xp: number; back: number; revive: number };
/** The hero panel's numbers: health, mana and XP as shares, and while it is down the seconds until it is back and the
 *  share of the wait still to run. */
export function heroView(sim: Sim, p: number): HeroView | null {
  const h = sim.players[p]?.hero;
  if (!h) return null;
  const alive = h.i >= 0, st = heroStats(h.id, h.level, h.whet), wait = Math.max(0, h.respawn - sim.time);
  return {
    alive, level: h.level,
    hp: alive ? sim.hp[h.i] / sim.maxHp[h.i] : 0, mana: alive ? h.mana / st.mana : 0,
    xp: h.level >= HERO_RULES.maxLevel ? 1 : h.xp / HERO_RULES.xpToNext(h.level),
    back: alive ? 0 : Math.max(1, Math.ceil(wait)), revive: alive ? 0 : Math.min(1, wait / HERO_RULES.revive(h.level)),
  };
}

/** Gold a player has earned this match, from the sim's own count (gold leaves by tiers and the center's shop now, so it
 *  can't be worked out from what they hold). */
export function goldEarned(sim: Sim, p: number) { return Math.round(sim.players[p].earned); }

export type EndRow = { p: number; name: string; army: string; kills: number; gold: number; tier: number; base: string; standing: boolean; you: boolean };
/** One row a player for the end card. `fellAt[p]` is the match time that player's barracks fell (screens.ts records it
 *  from the sim's 'out' events). Winner first, then by when they fell, latest first. */
export function endRows(sim: Sim, me: number, fellAt: (number | null)[]): EndRow[] {
  return sim.players.map((pl, p) => ({
    p, name: who(p, me), army: pl.faction ? FACTION[pl.faction].name : 'Footmen', kills: pl.kills, gold: goldEarned(sim, p), tier: pl.tier,
    standing: !pl.out,
    base: pl.out ? `Fell at ${mmss(fellAt[p] ?? sim.time)}` : `${Math.max(1, Math.round((pl.baseHp / pl.baseMax) * 100))}% left`,
    you: p === me,
  })).sort((a, b) => (b.p === sim.winner ? 1 : 0) - (a.p === sim.winner ? 1 : 0) || Number(b.standing) - Number(a.standing) || (fellAt[b.p] ?? 0) - (fellAt[a.p] ?? 0));
}

export type EndWords = { title: string; line: string; tone: 'won' | 'lost' | 'watch' };
/** The end card's headline and line. `winner` -1 means the match goes on (you fell before it was decided). */
export function endWords(sim: Sim, me: number): EndWords {
  const w = sim.winner;
  if (w < 0) return { title: 'Defeat', line: `Base fell at ${mmss(sim.time)}. Watch the others fight.`, tone: 'lost' };
  const line = `${w === me ? 'Your army' : TEAM_NAME[w]} wins at ${mmss(sim.time)}.`;
  if (me < 0) return { title: `${TEAM_NAME[w]} wins`, line, tone: 'watch' };
  return w === me ? { title: 'Victory', line, tone: 'won' } : { title: 'Defeat', line, tone: 'lost' };
}

/** Classes a slot can take at this tier (never the Footman once you have an army), lowest tier first. */
export function slotChoices(tier: number): ClassId[] {
  return (Object.keys(CLASS) as ClassId[]).filter((c) => c !== 'footman' && CLASS[c].maxSlots > 0 && CLASS[c].tier <= tier).sort((a, b) => CLASS[a].tier - CLASS[b].tier);
}

export type HeroCard = { id: HeroId; name: string; short: string; role: string; lines: { name: string; text: string; when: string }[] };
/** The heroes as the pick shows them: name, role, then the passive and both skills with when each one opens. */
export const HERO_CARDS: HeroCard[] = HEROES.map((id) => {
  const d = HERO[id];
  return { id, name: d.name, short: d.short, role: d.role, lines: [
    { name: d.passive.name, text: friendly(d.passive.text), when: 'Always' },
    ...d.skills.map((k) => ({ name: k.name, text: friendly(k.text), when: k.level > 1 ? `Level ${k.level}` : 'From the start' })),
  ] };
});
